// Builds the title given to a Bunny video at upload time. Pure — no React, no remote —
// so it can be unit-tested directly (mirrors videoAnalyticsUtils).
//
// The naming rule (the prefix and which entity parameters to include) is schema config,
// read from rules.settings["video-options"] on a `dataType: video` element; the frontend
// owns only the invariant `_{timestamp}_{filename}` tail. That split matters because one
// component (DynamicDetails) renders the video field at two schema levels with disjoint
// context: a GROUPING (manager/practice) and a FUND (Quarterly Commentary). A param whose
// source isn't in scope at the current level is dropped, so one resolver serves both.

export const VIDEO_TITLE_PARAMS = {
	group: (ctx) => ctx.groupName,                                                              // grouping level only
	portfolio: (ctx) => ctx.portfolio && (ctx.portfolio.fundNumber || ctx.portfolio.number),    // fund level only
	portfolioName: (ctx) => ctx.portfolio && (ctx.portfolio.fundName || ctx.portfolio.name),    // fund level only
	templateKey: (ctx) => ctx.templateKey,                                                      // both levels
};

// With no config, fall back to a level-neutral rule: whichever entity param is in scope
// still identifies the video in the Bunny library (Analytics → Videos shows the raw title
// for videos it can't attach to a document).
export const DEFAULT_TITLE_PARAMS = ['group', 'portfolio'];

// A titleParams entry of the form "lit:Some Text" contributes that text as a literal
// segment instead of resolving an entity. It exists so a config can reproduce a fixed
// segment another upload path already emits — e.g. VideoUploadPanel's template name —
// keeping directly-uploaded and promoted videos on one naming pattern.
const LITERAL_PREFIX = 'lit:';

// Values go in as authored — spaces and all — to match the titles the manager-facing
// upload path already produces. Input is coerced rather than assumed to be a string:
// it comes from free-form JSON schema settings.
const clean = (s) => String(s ?? '').trim();

/**
 * `{titlePrefix}_{...titleParams}_{timestamp}_{filename}`, e.g.
 * `AboutManagerVideo_Aequitas_2026-08-10T02:11:00.000Z_intro.mp4`.
 *
 *   options  — the element's "video-options" ({} when unconfigured).
 *   ctx      — { groupName, portfolio, templateKey } for the entity being edited.
 *   fileName — the uploaded file's name, appended raw.
 */
export function buildVideoTitle(options, ctx, fileName) {
	const parts = [clean(options?.titlePrefix) || 'Video'];
	// titleParams is free-form JSON from the schema settings, so a mis-typed value must
	// not throw: this runs after the caller has already flipped its UI into "uploading",
	// and an escaping TypeError would strand that state with no error path.
	// An explicitly empty array means "prefix only"; only a missing/invalid one defaults.
	const params = Array.isArray(options?.titleParams) ? options.titleParams : DEFAULT_TITLE_PARAMS;
	params.forEach((param) => {
		const name = typeof param === 'string' ? param : '';
		let value;
		if (name.startsWith(LITERAL_PREFIX)) {
			value = clean(name.slice(LITERAL_PREFIX.length));
		} else {
			// Own-property check: a bare lookup would resolve inherited Object.prototype keys
			// ("toString", "constructor"), turning an unknown param into a junk segment.
			const resolve = Object.prototype.hasOwnProperty.call(VIDEO_TITLE_PARAMS, name)
				? VIDEO_TITLE_PARAMS[name]
				: null;
			value = resolve ? clean(resolve(ctx)) : '';
		}
		if (value) parts.push(value);
	});
	return `${parts.join('_')}_${new Date().toISOString()}_${fileName}`;
}
